import { createContext, Dispatch, useEffect } from "react";

import {
  getDispensaryUpdates,
  UpdateData,
} from "api/requests/getDispensaryUpdates";
import usePromiseReducer from "hooks/usePromiseReducer";
import useRouter from "hooks/useRouter";

export interface UpdateContextState {
  data: UpdateData;
  defaultParams: { skip: number; take: number };
  error: string;
  fetching: boolean;
  dispatch: { getUpdates: () => void };
  selectors: UpdateData;
}

export const initialState = {
  data: { totalCount: 0, updates: [] as UpdateData["updates"] },
  defaultParams: { skip: 0, take: 3 },
  dispatch: { getUpdates: () => {} },
  error: "",
  fetching: false,
  selectors: {
    totalCount: 0,
    updates: [] as UpdateData["updates"],
  },
};

const Context = createContext(initialState);

const { Provider } = Context;

const ACTION_TYPES = {
  ERROR: "ERROR",
  FETCH: "FETCH",
};

const useUpdateReducer = (state?: UpdateContextState) => {
  return usePromiseReducer(
    (
      state: UpdateContextState,
      action: { type: string; payload: { fetching: boolean; data: UpdateData } }
    ) => {
      switch (action.type) {
        case ACTION_TYPES.FETCH:
          return { ...state, ...action.payload, error: "" };
        case ACTION_TYPES.ERROR:
          return { ...state, ...action.payload, fetching: false };
        default:
          return state;
      }
    },
    { ...initialState, ...state }
  );
};

const fetchAction = (
  isFetching: boolean,
  data: UpdateData,
  dispatch?: Dispatch<{
    type: string;
    payload: { data: UpdateData; fetching: boolean };
  }>
) => {
  const state = {
    payload: { data, fetching: isFetching },
    type: ACTION_TYPES.FETCH,
  };

  if (dispatch) {
    dispatch(state);
  }
};

const errorAction = (
  error: string,
  dispatch?: Dispatch<{
    type: string;
    payload: { error: string };
  }>
) => {
  if (dispatch) {
    dispatch({ payload: { error }, type: ACTION_TYPES.ERROR });
  }
};

const getUpdatesAction = (
  dispensarySlug: string,
  dispatch?: Dispatch<{
    type: string;
    payload: { data: UpdateData; fetching: boolean } | { error: string };
  }>
) => {
  fetchAction(
    true,
    {
      totalCount: 0,
      updates: [],
    },
    dispatch
  );

  return getDispensaryUpdates(dispensarySlug)
    .then((response) => {
      const payload = {
        totalCount: response.metadata.totalCount,
        updates: response.data,
      };
      fetchAction(false, payload, dispatch);
      return payload;
    })
    .catch((e) => {
      errorAction(e?.message || "An unknown error occurred", dispatch);
      return Promise.reject(new Error(e?.message));
    });
};

const UpdateProvider = ({
  children,
  initialState,
}: {
  children: React.ReactElement;
  initialState: UpdateContextState;
}) => {
  const [state, dispatch] = useUpdateReducer(initialState);
  const {
    parsedQuery: { slug },
  } = useRouter();

  useEffect(() => {
    if (
      !initialState &&
      !state.fetching &&
      !state.error &&
      !state.data &&
      slug
    ) {
      getUpdatesAction(slug, dispatch);
    }
  }, [initialState, state, slug, dispatch]);

  const totalCount = state.data?.totalCount || 0;
  const updates = state.data?.updates;

  return (
    <Provider
      value={{
        ...state,
        dispatch: {
          getUpdates: () => getUpdatesAction(slug as string, dispatch),
        },
        selectors: {
          totalCount,
          updates,
        },
      }}
    >
      {children}
    </Provider>
  );
};

export { Context as default, getUpdatesAction, UpdateProvider };
